// apps/web/src/components/estoque/localizacao-select.tsx — Fase 13, Plano 09 (EST-08, D-21).
// Select reutilizável das localizações ATIVAS (GET /estoque/lookups/localizacoes), com Lock nas fixas.
// Usado por parâmetros de material, movimentação, conferência e inventário. `value` vazio = nenhuma (ou "Todas").
import { useEffect, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import type { LocalizacaoLookupEstoque } from '@pieltec/contracts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export interface LocalizacaoSelectProps {
  /** id da localização escolhida; '' = nenhuma (ou "Todas" quando `incluirTodas`). */
  readonly value: string;
  readonly onChange: (id: string) => void;
  /** Mostra a opção "Todas" (valor ''), para filtros. */
  readonly incluirTodas?: boolean;
  readonly rotulo?: string;
  readonly desabilitada?: boolean;
  readonly id?: string;
  readonly className?: string;
  /** Avisa as localizações carregadas (ex.: para mostrar o NOME do local escolhido numa ajuda de saldo). */
  readonly aoCarregar?: (locais: readonly LocalizacaoLookupEstoque[]) => void;
}

const VALOR_TODAS = '__todas__';

export function LocalizacaoSelect({ value, onChange, incluirTodas = false, rotulo = 'Localização', desabilitada = false, id, className, aoCarregar }: LocalizacaoSelectProps) {
  const [locais, setLocais] = useState<readonly LocalizacaoLookupEstoque[]>([]);
  const [falhou, setFalhou] = useState(false);
  const aoCarregarRef = useRef(aoCarregar);
  aoCarregarRef.current = aoCarregar;

  useEffect(() => {
    let vivo = true;
    fetch('/api/v1/estoque/lookups/localizacoes', { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<LocalizacaoLookupEstoque[]>;
      })
      .then((lista) => {
        if (!vivo) return;
        setLocais(lista);
        aoCarregarRef.current?.(lista);
      })
      .catch(() => {
        if (vivo) setFalhou(true);
      });
    return () => {
      vivo = false;
    };
  }, []);

  const valorInterno = value === '' ? (incluirTodas ? VALOR_TODAS : '') : value;

  return (
    <div className="flex flex-col gap-1">
      <Select
        value={valorInterno}
        onValueChange={(novo) => onChange(novo === VALOR_TODAS ? '' : novo)}
        disabled={desabilitada}
      >
        <SelectTrigger id={id} aria-label={rotulo} className={className ?? 'w-full'}>
          <SelectValue placeholder={falhou ? 'Não foi possível carregar' : 'Selecione a localização'} />
        </SelectTrigger>
        <SelectContent>
          {incluirTodas && <SelectItem value={VALOR_TODAS}>Todas</SelectItem>}
          {locais.map((local) => (
            <SelectItem key={local.id} value={local.id}>
              <span className="inline-flex items-center gap-2">
                {local.fixa && <Lock className="size-3" aria-label="Localização fixa" />}
                {local.nome}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {falhou && (
        <p className="text-xs text-rose-700" role="alert">
          Não foi possível carregar as localizações.
        </p>
      )}
    </div>
  );
}
